import { useState, useCallback, useRef } from 'preact/hooks';
import {
  conversationId,
  isSending,
  addPendingMessage,
  markPendingFailed,
  removePendingMessage,
  addConfirmedMessage,
  token,
} from '../store/state';
import { ApiError } from '../api/client';
import type { WidgetApiClient } from '../api/client';
import { getTokenManager } from './Widget';

interface MessageComposerProps {
  apiClient: WidgetApiClient | null;
  disabled: boolean;
}

/**
 * Generate a unique idempotency key (UUID v4)
 * Uses crypto.randomUUID() if available, falls back to manual generation
 */
function generateIdempotencyKey(): string {
  // Use native crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // Fallback: generate UUID v4 manually
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function MessageComposer({ apiClient, disabled }: MessageComposerProps) {
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  const handleInput = useCallback((e: Event) => {
    const target = e.target as HTMLTextAreaElement;
    setText(target.value);

    // Reset height to auto to get correct scrollHeight
    target.style.height = 'auto';
    // Set height to scrollHeight, max 120px
    target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
  }, []);

  // Send message
  const handleSend = useCallback(async () => {
    const trimmedText = text.trim();
    if (!trimmedText || !apiClient || !token.value || disabled) return;

    const idempotencyKey = generateIdempotencyKey();

    // Clear input immediately for better UX
    setText('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Add optimistic message
    addPendingMessage(idempotencyKey, trimmedText);
    isSending.value = true;

    try {
      const response = await apiClient.sendMessage({
        conversation_id: conversationId.value,
        text: trimmedText,
        idempotency_key: idempotencyKey,
      });

      // Store conversation_id from response (created on first message)
      if (response.conversation_id && !conversationId.value) {
        conversationId.value = response.conversation_id;
      }

      // Remove pending and add confirmed
      removePendingMessage(idempotencyKey);
      addConfirmedMessage({
        id: response.message_id,
        sender_type: 'customer',
        sender_operator_id: null,
        sanitized_text: response.sanitized_text,
        created_at: response.created_at,
      });
    } catch (err) {
      // On auth error: wait for token refresh, then retry once
      if (err instanceof ApiError && err.isAuthError()) {
        console.warn('[EasyHelp] Auth error on send, waiting for token refresh...');
        const tm = getTokenManager();
        if (tm) {
          try {
            const newToken = await tm.ensureValidToken();
            if (newToken && apiClient) {
              const response = await apiClient.sendMessage({
                conversation_id: conversationId.value,
                text: trimmedText,
                idempotency_key: idempotencyKey,
              });

              if (response.conversation_id && !conversationId.value) {
                conversationId.value = response.conversation_id;
              }

              removePendingMessage(idempotencyKey);
              addConfirmedMessage({
                id: response.message_id,
                sender_type: 'customer',
                sender_operator_id: null,
                sanitized_text: response.sanitized_text,
                created_at: response.created_at,
              });
              return; // Success on retry
            }
          } catch (retryErr) {
            console.error('[EasyHelp] Retry after token refresh failed:', retryErr);
          }
        }
      }

      console.error('[EasyHelp] Failed to send message:', err);
      markPendingFailed(idempotencyKey);
    } finally {
      isSending.value = false;
    }
  }, [text, apiClient, disabled]);

  // Handle key press
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Send on Enter (without Shift)
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const isDisabled = disabled || !text.trim() || isSending.value || !token.value;

  return (
    <div class="easyhelp-composer">
      <form
        class="easyhelp-composer-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSend();
        }}
      >
        <textarea
          ref={textareaRef}
          class="easyhelp-composer-input"
          placeholder={disabled ? 'You are offline...' : 'Type a message...'}
          value={text}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          rows={1}
        />

        <button
          type="submit"
          class="easyhelp-composer-send"
          disabled={isDisabled}
          aria-label="Send message"
        >
          {isSending.value ? (
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="2"
                opacity="0.25"
              />
              <path
                d="M12 2a10 10 0 0 1 10 10"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 12 12"
                  to="360 12 12"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </path>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          )}
        </button>
      </form>
    </div>
  );
}
